/**
 * Account (S90) and its screens: business details, What's yours (S91), Team (S92), Billing (S94), Cancel (S95),
 * Notifications (S96), Security (S97), Download everything (S98), Help (S99) and Appearance.
 */
import { Banner, Button, Card, Checkbox, ChoiceGroup, Chip, EmptyState, Icon, KeyValue, List, Row, Segmented, Sheet, SourceBadge, Switch, TextField, type IconName } from '@ff/app-ui';
import { Email, type ClientAction, type account as C } from '@ff/contracts';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { z } from 'zod';
import { errorText, ownWords } from '../lib/api.ts';
import { invalidate, send, setCached, useQuery, useSend } from '../lib/data.ts';
import { ago, bytesLabel, dayLabel, dayShort, longDate, money } from '../lib/format.ts';
import { navigate } from '../lib/router.ts';
import { can, signOut, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { applyTheme, haptic, theme, toast, tz, type ThemePref } from '../lib/ui.ts';
import { PushCard } from '../pwa/cards.tsx';
import { S } from '@ff/app-ui/copy';
import { Loaded, Screen, Section } from '../ui/common.tsx';
import { FactValue } from '../ui/FactValue.tsx';
import { provenance } from '../lib/facts.ts';
import { createPasskey, passkeysSupported } from '../ui/webauthn.ts';

declare const __FF_DEMO__: boolean;

interface AccRow {
  key: keyof typeof S.account.rows;
  href: string;
  icon: IconName;
  action?: ClientAction;
}

const ROWS: AccRow[][] = [
  [
    { key: 'business', href: '/account/business', icon: 'building', action: 'facts.read' },
    { key: 'hours', href: '/hours', icon: 'clock', action: 'hours.read' },
    // No site banner row: the emergency banner is hidden until it works (deck ruling c079).
    { key: 'reviewEmails', href: '/reviews/templates', icon: 'mail', action: 'review_templates.edit' },
    { key: 'reports', href: '/reports', icon: 'file', action: 'reports.read' },
  ],
  [
    { key: 'team', href: '/account/team', icon: 'users', action: 'team.read' },
    { key: 'billing', href: '/account/billing', icon: 'card', action: 'billing.read' },
    { key: 'ownership', href: '/account/ownership', icon: 'shield', action: 'home.read' },
    { key: 'export', href: '/account/export', icon: 'download', action: 'export.create' },
  ],
  [
    { key: 'notifications', href: '/account/notifications', icon: 'bell' },
    { key: 'security', href: '/account/security', icon: 'lock' },
    { key: 'appearance', href: '/account/appearance', icon: 'moon' },
    { key: 'help', href: '/help', icon: 'help' },
  ],
];

export function AccountScreen(): JSX.Element {
  const s = useReady();
  const m = s.membership;
  const billing = useQuery('billing', {}, { enabled: can(m, 'billing.read') });
  const [confirm, setConfirm] = useState(false);
  const payProblem = billing.data?.status === 'past_due' || billing.data?.status === 'work_paused';
  return (
    <Screen title={S.account.title}>
      <div class="stack" data-gap="l">
        <Card>
          <p class="t-heading">{s.me.user.name ?? s.me.user.email}</p>
          <p class="t-muted">{S.account.signedInAs(s.me.user.email)}</p>
          <div class="cluster" style="margin-top:8px">
            <Chip tone="outline">{S.account.role[m.role]}</Chip>
            <span class="t-small t-muted">{m.tenantName}</span>
          </div>
        </Card>
        {ROWS.map((group, gi) => {
          const rows = group.filter((r) => !r.action || can(m, r.action));
          if (!rows.length) return null;
          return (
            <List key={gi}>
              {rows.map((r) => (
                <li key={r.key}>
                  <Row
                    href={r.href}
                    lead={<Icon name={r.icon} size={22} />}
                    title={S.account.rows[r.key]}
                    meta={S.account.rowsHint[r.key]}
                    chip={r.key === 'billing' && payProblem ? <Chip tone="danger">{S.account.paymentProblem}</Chip> : null}
                  />
                </li>
              ))}
            </List>
          );
        })}
        <Button variant="secondary" icon="logout" block onClick={() => setConfirm(true)}>
          {S.account.rows.signOut}
        </Button>
        <p class="t-small t-muted center">{S.account.appVersion(`1.0${__FF_DEMO__ ? ' demo' : ''}`)}</p>
      </div>
      <Sheet
        open={confirm}
        onClose={() => setConfirm(false)}
        title={S.account.rows.signOut}
        closeLabel={S.app.cancel}
        footer={
          <Button variant="primary" block onClick={() => void signOut()}>
            {S.account.rows.signOut}
          </Button>
        }
      >
        <p>{S.account.signOutConfirm}</p>
      </Sheet>
    </Screen>
  );
}

export function BusinessScreen(): JSX.Element {
  const q = useQuery('facts');
  return (
    <Screen title={S.account.business} back="/account" lead={S.account.businessLead} query={q}>
      <Loaded q={q} skeleton="cards">
        {(d) => (
          <div class="stack" data-gap="l">
            {d.groups.map((g) => (
              <Section key={g.group} title={S.getLive.group[g.group]!} id={`b-${g.group}`}>
                <Card>
                  <dl class="kv" data-stack>
                    {g.facts.map((f) => (
                      <>
                        <dt key={`${f.id}k`}>{f.label}</dt>
                        <dd key={`${f.id}v`}>
                          <FactValue path={f.path} value={f.value} />
                          <SourceBadge icon={f.source.kind === 'register' ? 'shield' : f.source.kind === 'gbp' ? 'globe' : 'user'}>{provenance(f.source, f.checkedAt)}</SourceBadge>
                        </dd>
                      </>
                    ))}
                  </dl>
                </Card>
              </Section>
            ))}
            <Button href="/inbox/new" icon="edit">{S.account.askChange}</Button>
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

export function OwnershipScreen(): JSX.Element {
  const s = useReady();
  const q = useQuery('ownership');
  return (
    <Screen title={S.ownership.title} back="/account" query={q}>
      <Loaded q={q}>
        {(o) => (
          <div class="stack" data-gap="l">
            <ul role="list" class="own-list">
              <li><Icon name="globe" /><p>{o.domain.name ? S.ownership.domain(o.domain.name, o.domain.registrar) : S.ownership.noDomain}</p></li>
              <li><Icon name="pin" /><p>{o.googleProfile.youAreOwner ? S.ownership.profile : S.ownership.profileUnknown}</p></li>
              <li><Icon name="file" /><p>{S.ownership.site}</p></li>
              <li><Icon name="download" /><p>{S.ownership.data}</p></li>
              <li><Icon name="users" /><p>{S.ownership.customers}</p></li>
            </ul>
            {can(s.membership, 'export.create') && (
              <Button variant="primary" href="/account/export" icon="download">
                {S.ownership.download}
              </Button>
            )}
            {can(s.membership, 'requests.create') && (
              <Button
                variant="secondary"
                icon="inbox"
                onClick={() => {
                  try {
                    sessionStorage.setItem('ff-request-prefill', S.ownership.buyoutText);
                  } catch {
                    /* private mode: the request screen opens empty */
                  }
                  navigate('/inbox/new');
                }}
              >
                {S.ownership.buyout}
              </Button>
            )}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ team

type Role = 'owner' | 'manager' | 'crew' | 'bookkeeper';

export function TeamScreen(): JSX.Element {
  const s = useReady();
  const zone = useStore(tz);
  const q = useQuery('team');
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<z.infer<typeof C.TeamMember> | null>(null);
  const isOwner = can(s.membership, 'team.manage');
  return (
    <Screen title={S.team.title} back="/account" lead={S.team.lead} query={q}>
      <Loaded q={q}>
        {(t) => (
          <div class="stack" data-gap="l">
            <List>
              {t.members.map((m) => (
                <li key={m.userId}>
                  <Row
                    onClick={isOwner && !m.isYou ? () => setEditing(m) : undefined}
                    lead={<Icon name="user" size={22} />}
                    title={
                      <>
                        {m.name ?? m.email}
                        {m.isYou && <span class="t-muted"> ({S.team.you})</span>}
                      </>
                    }
                    meta={[m.email, m.lastActiveAt ? S.team.lastActive(ago(m.lastActiveAt, zone)) : S.team.neverActive].join(' · ')}
                    chip={
                      <span class="cluster" style="justify-content:flex-end">
                        <Chip tone="outline">{S.account.role[m.role]}</Chip>
                        {m.role === 'manager' && m.canApprove && <Chip tone="ok">{S.team.canApprove}</Chip>}
                      </span>
                    }
                  />
                </li>
              ))}
            </List>
            {t.invites.length > 0 && (
              <Section title={S.team.pending} id="invites">
                <List>
                  {t.invites.map((i) => (
                    <li key={i.id}>
                      <div class="row">
                        <span class="row-lead"><Icon name="mail" size={22} /></span>
                        <span class="row-main">
                          <span class="row-title">{i.email}</span>
                          <span class="row-meta" style="display:block">{`${S.account.role[i.role]} · ${S.team.expires(dayShort(i.expiresAt.slice(0, 10)))}`}</span>
                        </span>
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={async () => {
                            await send('teamInviteRevoke', { params: { id: i.id } }).catch(() => undefined);
                            invalidate('team');
                            toast(S.team.withdrawn);
                          }}
                        >
                          {S.team.withdraw}
                        </Button>
                      </div>
                    </li>
                  ))}
                </List>
              </Section>
            )}
            {can(s.membership, 'team.invite_crew') && (
              <>
                {!isOwner && <p class="t-small t-muted">{S.team.managerCanInviteCrew}</p>}
                <Button variant="primary" icon="plus" onClick={() => setInviting(true)}>
                  {S.team.invite}
                </Button>
              </>
            )}
          </div>
        )}
      </Loaded>
      <InviteSheet open={inviting} onClose={() => setInviting(false)} ownerView={isOwner} />
      <MemberSheet member={editing} onClose={() => setEditing(null)} />
    </Screen>
  );
}

function InviteSheet({ open, onClose, ownerView }: { open: boolean; onClose: () => void; ownerView: boolean }): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(ownerView ? 'manager' : 'crew');
  const [approve, setApprove] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const act = useSend();
  const roles: Role[] = ownerView ? ['manager', 'crew', 'bookkeeper'] : ['crew'];
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={S.team.inviteTitle}
      closeLabel={S.app.close}
      footer={
        <Button
          variant="primary"
          block
          busy={act.busy}
          onClick={async () => {
            const e = Email.safeParse(email.trim());
            if (!e.success) return setErr(S.signin.emailError);
            setErr(null);
            const out = await act.run(() => send('teamInvite', { body: { email: e.data, role, ...(role === 'manager' && approve ? { canApprove: true } : {}) } }));
            if (out) {
              invalidate('team');
              toast(S.team.sent(out.email));
              setEmail('');
              onClose();
            }
          }}
        >
          {S.team.send}
        </Button>
      }
    >
      <div class="stack">
        <TextField label={S.team.email} type="email" inputMode="email" autoCapitalize="none" value={email} onInput={setEmail} error={err} />
        <ChoiceGroup legend={S.team.role} value={role} onChange={setRole} options={roles.map((r) => ({ value: r, label: S.account.role[r]!, detail: S.team.roleDetail[r] }))} />
        {role === 'manager' && ownerView && <Checkbox checked={approve} onChange={setApprove}>{S.team.canApprove}. {S.team.canApproveDetail}</Checkbox>}
        {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
      </div>
    </Sheet>
  );
}

function MemberSheet({ member, onClose }: { member: z.infer<typeof C.TeamMember> | null; onClose: () => void }): JSX.Element {
  const [role, setRole] = useState<Role>('manager');
  const [approve, setApprove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const act = useSend();
  useEffect(() => {
    if (member) {
      setRole(member.role);
      setApprove(member.canApprove);
      setRemoving(false);
    }
  }, [member?.userId]);
  const name = member?.name ?? member?.email ?? '';
  return (
    <Sheet
      open={!!member}
      onClose={onClose}
      title={S.team.edit(name)}
      closeLabel={S.app.close}
      footer={
        removing ? (
          <div class="stack" data-gap="s">
            <p>{S.team.removeConfirm(name)}</p>
            <Button
              variant="danger-solid"
              block
              busy={act.busy}
              onClick={async () => {
                if (await act.run(() => send('teamMemberRemove', { params: { user: member!.userId } }))) {
                  invalidate('team');
                  toast(S.team.removed(name));
                  onClose();
                }
              }}
            >
              {S.team.removePerson(name)}
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            block
            busy={act.busy}
            onClick={async () => {
              const out = await act.run(() => send('teamMemberUpdate', { params: { user: member!.userId }, body: { role, canApprove: role === 'manager' ? approve : false } }));
              if (out) {
                invalidate('team');
                toast(S.team.saved);
                onClose();
              }
            }}
          >
            {S.app.save}
          </Button>
        )
      }
    >
      <div class="stack">
        <ChoiceGroup legend={S.team.role} value={role} onChange={setRole} options={(['manager', 'crew', 'bookkeeper'] as Role[]).map((r) => ({ value: r, label: S.account.role[r]!, detail: S.team.roleDetail[r] }))} />
        {role === 'manager' && <Switch label={S.team.canApprove} detail={S.team.canApproveDetail} checked={approve} onChange={setApprove} />}
        {!removing && (
          <Button variant="danger" icon="trash" onClick={() => setRemoving(true)}>
            {S.team.removePerson(name)}
          </Button>
        )}
        {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------------------------------ billing

export function BillingScreen(): JSX.Element {
  const s = useReady();
  const m = s.membership;
  const q = useQuery('billing', {}, { enabled: can(m, 'billing.read') });
  const inv = useQuery('billingInvoices', {}, { enabled: can(m, 'billing.read') });
  const portal = useSend();
  if (!can(m, 'billing.read'))
    return (
      <Screen title={S.billing.title} back="/account">
        <EmptyState title={S.billing.title} body={S.billing.noPermission} />
      </Screen>
    );
  const openPortal = async () => {
    const out = await portal.run(() => send('billingPortal'));
    if (out) window.location.assign(out.url);
  };
  return (
    <Screen title={S.billing.title} back="/account" query={q}>
      <Loaded q={q}>
        {(b) => (
          <div class="stack" data-gap="l">
            {b.banner && (
              <Banner
                tone={b.banner.kind === 'payment_failed' || b.banner.kind === 'work_paused' ? 'danger' : 'info'}
                role={b.banner.kind === 'payment_failed' || b.banner.kind === 'work_paused' ? 'alert' : 'status'}
                title={b.banner.text}
                action={(b.banner.kind === 'payment_failed' || b.banner.kind === 'work_paused') && can(m, 'billing.manage') ? <Button size="sm" variant="primary" busy={portal.busy} onClick={() => void openPortal()}>{S.billing.updateCard}</Button> : null}
              />
            )}
            {b.canKeepPlan && b.cancelEffectiveOn && can(m, 'billing.cancel') && <KeepPlan lastDay={b.cancelEffectiveOn} />}
            <Card>
              <div class="card-head">
                <h2 class="t-heading">{S.billing.plan}</h2>
                <Chip tone={b.status === 'active' ? 'ok' : b.status === 'past_due' || b.status === 'work_paused' ? 'danger' : 'neutral'}>{S.billing.status[b.status]}</Chip>
              </div>
              <p>{S.billing.planLine(b.planLabel, money(b.amountCents))}</p>
              <KeyValue
                rows={[
                  ...(b.nextCharge ? ([[S.billing.nextCharge, S.billing.nextChargeLine(money(b.nextCharge.amountCents), dayShort(b.nextCharge.on))]] as [string, string][]) : []),
                  ...(b.minTermEndsOn ? ([[S.billing.minTerm, dayShort(b.minTermEndsOn)]] as [string, string][]) : []),
                  [S.billing.card, b.card ? S.billing.cardLine(b.card.brand.replace(/^\w/, (c) => c.toUpperCase()), b.card.last4) : S.billing.noCard],
                ]}
              />
              {can(m, 'billing.manage') && (
                <div class="card-foot stack" data-gap="s">
                  <Button icon="card" busy={portal.busy} onClick={() => void openPortal()}>
                    {S.billing.updateCard}
                  </Button>
                  <p class="t-small t-muted">{S.billing.stripeNote}</p>
                  {portal.error && <p class="field-error" role="alert">{errorText(portal.error, S.errors.offlineSend)}</p>}
                </div>
              )}
            </Card>
            {b.credits.length > 0 && (
              <Section title={S.billing.credits} id="credits">
                <List>
                  {b.credits.map((c) => (
                    <li key={c.id}>
                      <Row title={c.reason} meta={dayShort(c.createdAt.slice(0, 10))} chip={<span class="t-data">{money(c.amountCents)}</span>} />
                    </li>
                  ))}
                </List>
              </Section>
            )}
            <Section title={S.billing.invoices} id="invoices">
              {inv.data?.items.length ? (
                <List>
                  {inv.data.items.map((i) => (
                    <li key={i.id}>
                      <Row href={i.pdfUrl ?? undefined} external={!!i.pdfUrl} lead={<Icon name="receipt" size={22} />} title={i.number ?? i.id} meta={dayShort(i.date)} chip={<span class="cluster"><span class="t-data">{money(i.amountCents)}</span><Chip tone={i.status === 'paid' ? 'ok' : i.status === 'open' ? 'warn' : 'muted'}>{S.billing.invoiceStatus[i.status]}</Chip></span>} />
                    </li>
                  ))}
                </List>
              ) : (
                <p class="t-muted">{S.billing.invoicesEmpty}</p>
              )}
            </Section>
            {can(m, 'billing.cancel') && b.status !== 'canceling' && b.status !== 'ended' && (
              <Button variant="quiet" href="/account/billing/cancel">
                {S.billing.cancel}
              </Button>
            )}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

type Reason = z.infer<typeof C.CancelReason>;
const REASONS: Reason[] = ['too_expensive', 'no_results', 'closing_or_selling', 'other'];
const PAUSE_DAYS = ['7', '14', '30'] as const;

/** "Keep my plan" (deck ruling c104): undo a pending cancel before the last day. Owner only, with step-up. */
function KeepPlan({ lastDay, onKept }: { lastDay: string; onKept?: () => void }): JSX.Element {
  const act = useSend();
  return (
    <Card>
      <p>{S.billing.keepPlanLead(dayShort(lastDay))}</p>
      <div class="card-foot stack" data-gap="s">
        <Button
          variant="primary"
          icon="check"
          busy={act.busy}
          onClick={async () => {
            if (!(await act.run(() => send('billingKeepPlan', { body: { confirm: true } })))) return;
            haptic();
            invalidate('billing', 'home');
            toast(S.billing.kept);
            onKept?.();
          }}
        >
          {S.billing.keepPlan}
        </Button>
        {act.error && <p class="field-error" role="alert">{ownWords(act.error, S.errors.offlineSend)}</p>}
      </div>
    </Card>
  );
}

/**
 * S95: two screens with exact dates. Screen 2 has the reason chips, then the save offer (a pause, deck ruling c031)
 * beside a Cancel button of equal size (California click to cancel). The cancel, the pause and Keep my plan each
 * send their email (c104).
 */
export function CancelScreen(): JSX.Element {
  const [stage, setStage] = useState<1 | 2 | 'canceled' | 'paused'>(1);
  const [reason, setReason] = useState<Reason | null>(null);
  const [days, setDays] = useState<(typeof PAUSE_DAYS)[number]>('30');
  const [preview, setPreview] = useState<z.infer<typeof C.CancelPreview> | null>(null);
  const [lastDay, setLastDay] = useState<string | null>(null);
  const [pausedUntil, setPausedUntil] = useState<string | null>(null);
  const load = useSend();
  const cancel = useSend();
  const pause = useSend();
  useEffect(() => {
    void load.run(() => send('billingCancelPreview')).then((p) => p && setPreview(p));
  }, []);
  if (stage === 'canceled' && lastDay)
    return (
      <Screen title={S.billing.canceledTitle} back="/account/billing">
        <div class="stack" data-gap="l">
          <p class="t-heading" role="status">{S.billing.canceledLead(dayShort(lastDay))}</p>
          <KeepPlan lastDay={lastDay} onKept={() => navigate('/account/billing', { replace: true })} />
          <Button href="/account/export" icon="download">{S.ownership.download}</Button>
        </div>
      </Screen>
    );
  if (stage === 'paused' && pausedUntil)
    return (
      <Screen title={S.billing.pausedTitle} back="/account/billing">
        <p class="t-heading" role="status">{S.billing.pausedLead(dayShort(pausedUntil))}</p>
      </Screen>
    );
  const pauseOk = !!preview?.pause?.available;
  return (
    <Screen title={stage === 1 ? S.billing.cancelTitle : pauseOk ? S.billing.saveTitle : S.billing.cancelPlan} back="/account/billing">
      {!preview ? (
        load.error ? <Banner tone="danger" role="alert" title={errorText(load.error, S.errors.offlineSend)} action={<Button size="sm" variant="secondary" onClick={() => void load.run(() => send('billingCancelPreview')).then((p) => p && setPreview(p))}>{S.app.tryAgain}</Button>} /> : <div class="skel" data-h="card" aria-busy="true" />
      ) : stage === 1 ? (
        <div class="stack" data-gap="l">
          <Card>
            <ul class="bullets">
              <li><strong>{S.billing.lastDay(dayShort(preview.lastDay), S.billing.basis[preview.basis]!)}</strong></li>
              <li>{S.billing.untilThen}</li>
              <li>{S.billing.afterThat(dayShort(preview.siteKeptUntil))}</li>
              <li>{S.billing.downloadUntil(dayShort(preview.exportUntil))}</li>
            </ul>
          </Card>
          <Button href="/account/export" icon="download">{S.ownership.download}</Button>
          <Button variant="primary" size="lg" block onClick={() => setStage(2)}>{S.billing.continue}</Button>
        </div>
      ) : (
        <div class="stack" data-gap="l">
          <fieldset class="stack plain-set" data-gap="s">
            <legend class="sub-h">{S.billing.reason}</legend>
            <div class="chips-choice" role="group" aria-label={S.billing.reason}>
              {REASONS.map((r) => (
                <button key={r} type="button" class="filter" aria-pressed={reason === r} onClick={() => setReason(reason === r ? null : r)}>
                  {S.billing.reasons[r]}
                </button>
              ))}
            </div>
          </fieldset>
          {pauseOk ? (
            <Card>
              <p>{S.billing.pauseLead}</p>
              <p class="t-small t-muted">{S.billing.pauseOnce}</p>
              <div class="stack" data-gap="s" style="margin-top:12px">
                <Segmented label={S.billing.pauseLength} value={days} onChange={(v: (typeof PAUSE_DAYS)[number]) => setDays(v)} options={PAUSE_DAYS.map((d) => ({ value: d, label: S.billing.pauseDays[Number(d)]! }))} />
              </div>
            </Card>
          ) : preview.pause?.nextOn ? (
            <p class="t-muted">{S.billing.pauseUsed(dayShort(preview.pause.nextOn))}</p>
          ) : null}
          <div class="btn-row" data-equal>
            {pauseOk && (
              <Button
                size="lg"
                variant="secondary"
                busy={pause.busy}
                disabled={cancel.busy}
                onClick={async () => {
                  const out = await pause.run(() => send('billingPause', { body: { confirm: true, days: Number(days), ...(reason ? { reason } : {}) } }));
                  if (!out) return;
                  haptic();
                  invalidate('billing', 'home');
                  setPausedUntil(out.pause.until);
                  setStage('paused');
                }}
              >
                {S.billing.pausePlan}
              </Button>
            )}
            <Button
              size="lg"
              variant="secondary"
              busy={cancel.busy}
              disabled={pause.busy}
              onClick={async () => {
                const out = await cancel.run(() => send('billingCancel', { body: { confirm: true, ...(reason ? { reason } : {}) } }));
                if (!out) return;
                haptic();
                invalidate('billing', 'home');
                setLastDay(out.lastDay);
                setStage('canceled');
              }}
            >
              {S.billing.cancelPlan}
            </Button>
          </div>
          {preview.saveOffer && (
            <a class="section-link" href={preview.saveOffer.bookingUrl} target="_blank" rel="noopener noreferrer">
              {S.billing.book}
            </a>
          )}
          {(pause.error ?? cancel.error) && <p class="field-error" role="alert">{ownWords(pause.error ?? cancel.error, S.errors.offlineSend)}</p>}
        </div>
      )}
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ notification prefs

export function PrefsScreen(): JSX.Element {
  const q = useQuery('notificationPrefs', { query: {} });
  const save = async (body: z.input<typeof C.NotificationPrefsBody>) => {
    try {
      const out = await send('notificationPrefsUpdate', { query: {}, body });
      setCached('notificationPrefs', { query: {} }, () => out);
      toast(S.prefs.saved);
    } catch (e) {
      toast(errorText(e, S.errors.offlineSend));
    }
  };
  // Push and email only: no text channel is offered (deck rulings c022, c100).
  const toggle = (event: string, channel: 'push' | 'email', enabled: boolean) => {
    setCached('notificationPrefs', { query: {} }, (d) => (d ? { ...d, rows: d.rows.map((r) => (r.event === event ? { ...r, [channel]: { ...r[channel], enabled } } : r)) } : d));
    void save({ changes: [{ event: event as never, channel, enabled }] });
  };
  return (
    <Screen title={S.prefs.title} back="/account" lead={S.prefs.lead} query={q}>
      <div class="stack" data-gap="l">
        <PushCard />
        <Loaded q={q}>
          {(p) => (
            <div class="stack" data-gap="l">
              {p.rows.map((r) => (
                <Card key={r.event}>
                  <h2 class="t-heading pref-title">{r.label}</h2>
                  {(['push', 'email'] as const).map((ch) =>
                    r[ch].available ? (
                      <Switch key={ch} label={S.prefs[ch]} checked={r[ch].enabled} disabled={r[ch].locked} lockedReason={r[ch].locked ? r[ch].lockReason : null} onChange={(v) => toggle(r.event, ch, v)} />
                    ) : null,
                  )}
                </Card>
              ))}
              <Section title={S.prefs.quiet} id="quiet">
                <p class="t-muted">{S.prefs.quietLead}</p>
                <div class="time-pair">
                  <TextField label={S.prefs.quietFrom} type="time" value={p.quietHours.start} onInput={(v) => v && void save({ quietHours: { start: v, end: p.quietHours.end } })} />
                  <TextField label={S.prefs.quietTo} type="time" value={p.quietHours.end} onInput={(v) => v && void save({ quietHours: { start: p.quietHours.start, end: v } })} />
                </div>
              </Section>
              <Switch label={S.prefs.hideDetails} detail={S.prefs.hideDetailsDetail} checked={p.hideLockScreenDetails} onChange={(v) => void save({ hideLockScreenDetails: v })} />
            </div>
          )}
        </Loaded>
      </div>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ security

export function SecurityScreen(): JSX.Element {
  const s = useReady();
  const zone = useStore(tz);
  const sessions = useQuery('authSessions');
  const keys = useQuery('authPasskeyList');
  const act = useSend();
  const [confirmAll, setConfirmAll] = useState(false);
  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailErr, setEmailErr] = useState<string | null>(null);
  return (
    <Screen title={S.security.title} back="/account" query={sessions}>
      <div class="stack" data-gap="l">
        <Section title={S.security.passkeys} id="passkeys">
          <p class="t-muted">{S.security.passkeysLead}</p>
          <Loaded q={keys}>
            {(k) =>
              k.items.length ? (
                <List>
                  {k.items.map((p) => (
                    <li key={p.id}>
                      <Row lead={<Icon name="shield" size={22} />} title={p.label ?? S.security.unknownDevice} meta={p.lastUsedAt ? S.security.lastUsed(ago(p.lastUsedAt, zone), null) : ''} />
                    </li>
                  ))}
                </List>
              ) : (
                <p>{S.security.noPasskeys}</p>
              )
            }
          </Loaded>
          {passkeysSupported() && (
            <Button
              icon="plus"
              busy={act.busy}
              onClick={async () => {
                const ok = await act.run(async () => {
                  const opts = await send('authPasskeyRegisterOptions');
                  const credential = await createPasskey(opts);
                  return send('authPasskeyRegisterVerify', { body: { credential } });
                });
                if (ok) {
                  invalidate('authPasskeyList');
                  toast(S.signin.passkeyDone);
                } else toast(S.signin.passkeyFailed);
              }}
            >
              {S.security.addPasskey}
            </Button>
          )}
        </Section>

        <Section title={S.security.devices} id="devices">
          <Loaded q={sessions}>
            {(d) => (
              <List>
                {d.items.map((x) => (
                  <li key={x.ref}>
                    <div class="row">
                      <span class="row-lead"><Icon name={x.uaSummary?.includes('iPhone') || x.uaSummary?.includes('Android') ? 'phone' : 'globe'} size={22} /></span>
                      <span class="row-main">
                        <span class="row-title">{x.deviceLabel ?? x.uaSummary ?? S.security.unknownDevice}{x.current && <span class="t-muted"> · {S.security.thisDevice}</span>}</span>
                        <span class="row-meta" style="display:block">{S.security.lastUsed(ago(x.lastSeenAt, zone), x.lastCity)}</span>
                      </span>
                      {!x.current && (
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={async () => {
                            await send('authSessionRevoke', { params: { id: x.ref } }).catch(() => undefined);
                            invalidate('authSessions');
                            toast(S.security.signedOutDevice);
                          }}
                        >
                          {S.security.signOutDevice}
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </List>
            )}
          </Loaded>
          {confirmAll ? (
            <div class="stack" data-gap="s">
              <p>{S.security.signOutAllConfirm}</p>
              <div class="btn-row" data-equal>
                <Button variant="danger" onClick={async () => (await send('authSessionsRevokeAll').catch(() => undefined), void signOut())}>{S.security.signOutAll}</Button>
                <Button variant="quiet" onClick={() => setConfirmAll(false)}>{S.app.cancel}</Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" icon="logout" onClick={() => setConfirmAll(true)}>{S.security.signOutAll}</Button>
          )}
        </Section>

        <Section title={S.security.email} id="email">
          <p><strong>{s.me.user.email}</strong></p>
          {s.me.user.pendingEmailChange && <Banner tone="info" title={S.security.emailPending(s.me.user.pendingEmailChange.maskedNewEmail, longDate(s.me.user.pendingEmailChange.effectiveAt, zone))} />}
          {changing ? (
            <form
              class="stack"
              data-gap="s"
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                const em = Email.safeParse(newEmail.trim());
                if (!em.success) return setEmailErr(S.signin.emailError);
                setEmailErr(null);
                const out = await act.run(() => send('authEmailChange', { body: { newEmail: em.data } }));
                if (out) {
                  toast(S.security.emailPending(out.maskedNewEmail, longDate(out.effectiveAt, zone)));
                  setChanging(false);
                }
              }}
            >
              <p class="t-muted">{S.security.emailChangeLead}</p>
              <TextField label={S.security.newEmail} type="email" inputMode="email" autoCapitalize="none" value={newEmail} onInput={setNewEmail} error={emailErr} />
              <Button type="submit" variant="primary" busy={act.busy}>{S.security.changeEmail}</Button>
            </form>
          ) : (
            <Button icon="mail" onClick={() => setChanging(true)}>{S.security.changeEmail}</Button>
          )}
        </Section>
      </div>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ export, help, appearance

export function ExportScreen(): JSX.Element {
  const s = useReady();
  const zone = useStore(tz);
  const act = useSend();
  const [exp, setExp] = useState<z.infer<typeof C.ExportView> | null>(null);
  useEffect(() => {
    if (!exp || exp.status === 'ready' || exp.status === 'failed' || exp.status === 'expired') return;
    const t = setTimeout(() => void send('exportGet', { params: { id: exp.id } }).then(setExp, () => undefined), 3000);
    return () => clearTimeout(t);
  }, [exp]);
  if (!can(s.membership, 'export.create'))
    return (
      <Screen title={S.exportData.title} back="/account">
        <EmptyState title={S.exportData.title} body={S.exportData.onlyOwner} />
      </Screen>
    );
  return (
    <Screen title={S.exportData.title} back="/account" lead={S.exportData.lead}>
      <div class="stack form-narrow" data-gap="l">
        {!exp && (
          <Button variant="primary" size="lg" block icon="download" busy={act.busy} onClick={async () => setExp((await act.run(() => send('exportCreate', { body: { kind: 'everything' } }))) ?? null)}>
            {S.exportData.start}
          </Button>
        )}
        {exp && (exp.status === 'queued' || exp.status === 'building') && <Banner tone="info" icon="hourglass" role="status" title={S.exportData.building} />}
        {exp?.status === 'ready' && exp.downloadPath && (
          <Card>
            <p class="t-heading">{S.exportData.ready}</p>
            <div class="card-foot stack" data-gap="s">
              <Button variant="primary" href={exp.downloadPath} icon="download">{S.exportData.download(bytesLabel(exp.bytes ?? 0))}</Button>
              {exp.expiresAt && <p class="t-small t-muted">{S.exportData.expires(longDate(exp.expiresAt, zone))}</p>}
            </div>
          </Card>
        )}
        {(exp?.status === 'failed' || act.error) && <Banner tone="danger" role="alert" title={S.exportData.failed} />}
        <p class="t-small t-muted">{S.exportData.siteNote}</p>
      </div>
    </Screen>
  );
}

export function HelpScreen(): JSX.Element {
  const [question, setQuestion] = useState('');
  const act = useSend();
  const [answer, setAnswer] = useState<z.infer<typeof C.HelpAnswer> | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Screen title={S.help.title} back="/account">
      <div class="stack form-narrow" data-gap="l">
        <form
          class="stack"
          data-gap="s"
          onSubmit={async (e) => {
            e.preventDefault();
            if (question.trim().length < 3) return;
            setAnswer((await act.run(() => send('helpAsk', { body: { question: question.trim(), fromPath: '/help' } }))) ?? null);
          }}
        >
          <TextField label={S.help.search} type="search" value={question} onInput={setQuestion} enterKeyHint="search" maxLength={1000} />
          <Button type="submit" variant="primary" busy={act.busy} icon="send">{S.help.ask}</Button>
        </form>
        {answer && (
          <Card>
            {answer.answer ? (
              <div class="stack" data-gap="s">
                <p>{answer.answer.text}</p>
                {answer.answer.sources.length > 0 && <p class="t-small t-muted">{S.help.sources}: {answer.answer.sources.map((s) => s.title).join(', ')}</p>}
              </div>
            ) : (
              <div class="stack" data-gap="s">
                <p>{S.help.handedOff(answer.replyByLabel ?? '')}</p>
                {answer.requestId && <Button href={`/inbox/${answer.requestId}`} iconEnd="arrow-right">{S.help.openRequest}</Button>}
              </div>
            )}
          </Card>
        )}
        {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
        <Section title={S.help.common} id="faq">
          <ul role="list" class="faq">
            {S.help.faqs.map(([q, a], i) => (
              <li key={i}>
                <button type="button" class="faq-q" aria-expanded={open === i} aria-controls={`faq-${i}`} onClick={() => setOpen(open === i ? null : i)}>
                  <span>{q}</span>
                  <Icon name={open === i ? 'chevron-up' : 'chevron-down'} size={20} />
                </button>
                <p id={`faq-${i}`} class="faq-a" hidden={open !== i}>{a}</p>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </Screen>
  );
}

export function AppearanceScreen(): JSX.Element {
  const pref = useStore(theme);
  return (
    <Screen title={S.account.appearanceTitle} back="/account" lead={S.account.themeLead}>
      <div class="form-narrow">
        <Segmented label={S.account.appearanceTitle} value={pref} onChange={(v: ThemePref) => applyTheme(v)} options={(['system', 'light', 'dark'] as ThemePref[]).map((v) => ({ value: v, label: S.account.theme[v]! }))} />
      </div>
    </Screen>
  );
}

export { navigate };
